import { calculateXP, calculateLevel } from '../models/gamification';
/**
 * Un-completing a task, without corrupting everything downstream.
 *
 * Completing a task writes in five places: the task itself, a HabitInstance
 * if it recurs, the day's schedule, the stats record, and — through all of
 * those — analytics, reviews and goal progress.
 *
 * The saving grace is that only the first four are *stored*. Analytics,
 * reviews, the weekly widget and goal roll-ups are all derived on read from
 * tasks, habit instances and schedules. Correct those and everything
 * downstream follows on its own; there is nothing else to unwind. Stats are
 * the sole exception, because they are a running total, and that is where
 * every hard decision here lives.
 *
 * Three of those decisions are deliberate and worth stating:
 *
 *   - **Badges are never revoked.** You did reach 100 tasks. Taking the badge
 *     back because a total dipped would be punitive, and `earned_badges` is a
 *     set, so re-crossing the threshold can't duplicate it.
 *   - **longest_streak never goes down.** It is a record of something that
 *     happened, not a description of the present.
 *   - **current_streak is recomputed from history, never decremented.**
 *     Inverting the streak arithmetic is guesswork — undoing one of three
 *     completions on a day shouldn't touch the streak at all, and undoing the
 *     only one should end it. Deriving it from the dates that remain gets
 *     both right without a special case.
 */
/** Local YYYY-MM-DD, the format every date key in this app uses. */
function dayKey(date) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
function shiftDay(key, delta) {
    const [y, m, d] = key.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    date.setDate(date.getDate() + delta);
    return dayKey(date);
}
/**
 * Consecutive days of activity ending today — or yesterday, since a day you
 * haven't worked yet hasn't broken anything.
 */
export function currentStreakFromDates(dates, today) {
    const set = new Set(dates);
    let cursor;
    if (set.has(today))
        cursor = today;
    else if (set.has(shiftDay(today, -1)))
        cursor = shiftDay(today, -1);
    else
        return 0;
    let streak = 0;
    while (set.has(cursor)) {
        streak += 1;
        cursor = shiftDay(cursor, -1);
    }
    return streak;
}
/** The longest run of consecutive days anywhere in the history. */
export function longestStreakFromDates(dates) {
    const sorted = [...new Set(dates)].sort();
    let longest = 0;
    let run = 0;
    let previous = null;
    for (const day of sorted) {
        run = previous !== null && shiftDay(previous, 1) === day ? run + 1 : 1;
        if (run > longest)
            longest = run;
        previous = day;
    }
    return longest;
}
/**
 * How much XP to take back.
 *
 * `recorded` is what was actually awarded, stored at completion time. Falling
 * back to the formula is only for completions that predate that field: if the
 * task's difficulty or estimate has been edited since, recomputing returns a
 * different number and the total drifts. Recording it is the fix; the
 * fallback just stops old data from being worse than it needs to be.
 */
export function xpToReverse(task, recorded) {
    if (typeof recorded === 'number' && Number.isFinite(recorded) && recorded >= 0) {
        return recorded;
    }
    return calculateXP(task.difficulty, task.estimated_minutes, task.is_habit);
}
/** The stats record as it should look once a completion is taken back. */
export function statsAfterUndo(input) {
    const { stats, xpAwarded, isHabit, activeDatesAfter, today } = input;
    const totalXp = Math.max(0, (stats.total_xp ?? 0) - xpAwarded);
    const currentStreak = currentStreakFromDates(activeDatesAfter, today);
    const lastCompletion = [...activeDatesAfter].sort().at(-1) ?? null;
    return {
        ...stats,
        total_xp: totalXp,
        level: calculateLevel(totalXp),
        tasks_completed: Math.max(0, (stats.tasks_completed ?? 0) - 1),
        habits_completed: isHabit
            ? Math.max(0, (stats.habits_completed ?? 0) - 1)
            : stats.habits_completed,
        current_streak: currentStreak,
        // Never reduced: it records something that actually happened.
        longest_streak: Math.max(stats.longest_streak ?? 0, currentStreak),
        last_completion_date: lastCompletion,
        // Deliberately untouched. See the note at the top of this file.
        earned_badges: stats.earned_badges,
    };
}
/** The task as it should look once its completion is taken back. */
export function taskAfterUndo(task, now) {
    return {
        ...task,
        status: 'todo',
        completed_at: null,
        actual_minutes: null,
        // The record of what this completion paid goes with the completion.
        xp_awarded: null,
        updated_at: now,
    };
}
//# sourceMappingURL=undo.js.map